import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import imageCompression from "browser-image-compression";
import {
  createSponsor,
  deleteSponsorById,
  fetchSponsors as fetchSponsorsRequest,
  updateSponsor,
} from "../../api/sponsors";
import SponsorForm from "./components/SponsorForm";
import SponsorList from "./components/SponsorList";

const initialSponsor = {
  business_name: "",
  address: "",
  phone: "",
  phone_lada: "+54",
  notes: "",
};

const countryCodes = [
  { code: "+54", label: "ARG" },
  { code: "+52", label: "MEX" },
  { code: "+1", label: "USA" },
  { code: "+57", label: "COL" },
  { code: "+34", label: "ESP" },
];

const initialLink = { title: "", url: "" };

const stripKnownLada = (phone) => {
  const normalizedPhone = phone ?? "";
  const knownPrefix = countryCodes.find(({ code }) =>
    normalizedPhone.startsWith(code),
  );
  return {
    lada: knownPrefix?.code ?? "+54",
    phone: knownPrefix
      ? normalizedPhone.slice(knownPrefix.code.length)
      : normalizedPhone,
  };
};

export default function SponsorPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState(initialSponsor);
  const [linkForm, setLinkForm] = useState(initialLink);
  const [editingId, setEditingId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalErrors, setGeneralErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sponsorToDelete, setSponsorToDelete] = useState(null);
  const [links, setLinks] = useState([]);
  const [expandedSponsorId, setExpandedSponsorId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 9;
  const filteredSponsors = sponsors.filter((sponsor) =>
    `${sponsor.business_name}`.toLowerCase().includes(searchTerm),
  );
  const indexOfLastSponsor = currentPage * itemsPerPage;
  const indexOfFirstSponsor = indexOfLastSponsor - itemsPerPage;
  const currentSponsors = filteredSponsors.slice(
    indexOfFirstSponsor,
    indexOfLastSponsor,
  );
  const totalPages = Math.ceil(filteredSponsors.length / itemsPerPage);

  const refreshSponsors = async () => {
    try {
      const sponsorList = await fetchSponsorsRequest();
      setSponsors(sponsorList);
    } catch (error) {
      if (error.status === 404) {
        setSponsors([]);
        return;
      }

      throw error;
    }
  };

  useEffect(() => {
    const loadSponsors = async () => {
      try {
        await refreshSponsors();
      } catch (err) {
        console.error("Error al cargar patrocinadores", err);
        enqueueSnackbar(err.message || "Error al cargar patrocinadores", {
          variant: "error",
        });
      }
    };

    loadSponsors();
  }, [enqueueSnackbar]);

  const resetForm = () => {
    setForm(initialSponsor);
    setLinkForm(initialLink);
    setPhotoFile(null);
    setEditingId(null);
    setLinks([]);
    setFieldErrors({});
    setGeneralErrors([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 5,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      });
      setPhotoFile(compressedFile);
    } catch (error) {
      console.error("Error al comprimir la imagen:", error);
      enqueueSnackbar("No se pudo comprimir la imagen. Intenta con otra.", {
        variant: "error",
      });
    }
  };

  const handleLinkChange = (e) => {
    const { name, value } = e.target;
    setLinkForm({ ...linkForm, [name]: value });
    setFieldErrors((prev) => ({
      ...prev,
      [name === "title" ? "link_title" : "link_url"]: "",
      links: "",
    }));
  };

  const handleAddLink = () => {
    const nextFieldErrors = {};

    if (!linkForm.title.trim()) {
      nextFieldErrors.link_title = "El titulo del enlace es obligatorio.";
    }

    if (!linkForm.url.trim()) {
      nextFieldErrors.link_url = "La URL del enlace es obligatoria.";
    } else {
      try {
        new URL(linkForm.url);
      } catch {
        nextFieldErrors.link_url = "Ingresa una URL valida.";
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...nextFieldErrors }));
      return;
    }

    setLinks((prev) => [...prev, linkForm]);
    setLinkForm(initialLink);
  };

  const handleDeleteLink = (index) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const nextFieldErrors = {};

    if (!form.business_name.trim()) {
      nextFieldErrors.business_name = "El nombre del negocio es obligatorio.";
    }
    if (!form.phone.trim()) {
      nextFieldErrors.phone = "El telefono es obligatorio.";
    } else if (!/^\d+$/.test(form.phone.trim())) {
      nextFieldErrors.phone = "El telefono solo debe contener numeros.";
    }
    if (!form.address.trim()) {
      nextFieldErrors.address = "La direccion es obligatoria.";
    }
    if (!photoFile && !editingId) {
      nextFieldErrors.photo = "La imagen del patrocinador es obligatoria.";
    }
    if (photoFile) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!validTypes.includes(photoFile.type)) {
        nextFieldErrors.photo = "La foto debe ser JPG o PNG.";
      }
      if (photoFile.size > 5120 * 1024) {
        nextFieldErrors.photo = "La foto no debe exceder los 5 MB.";
      }
    }
    setFieldErrors(nextFieldErrors);
    setGeneralErrors([]);
    return Object.keys(nextFieldErrors).length === 0;
  };

  const buildSponsorPayload = () => {
    const basePayload = Object.entries(form).reduce((payload, [key, value]) => {
      if (key === "phone_lada") {
        return payload;
      }

      payload[key] = value;
      return payload;
    }, {});

    basePayload.phone = `${form.phone_lada}${form.phone}`;

    if (!photoFile) {
      return basePayload;
    }

    const formData = new FormData();
    Object.entries(basePayload).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("photo", photoFile);

    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = buildSponsorPayload();

      if (editingId) {
        await updateSponsor(editingId, payload, links);
        enqueueSnackbar("Patrocinador actualizado correctamente.", {
          variant: "success",
        });
      } else {
        await createSponsor(payload, links);
        enqueueSnackbar("Patrocinador agregado correctamente.", {
          variant: "success",
        });
      }

      resetForm();
      await refreshSponsors();
    } catch (err) {
      if (err.errors) {
        const messages = Object.values(err.errors).flat();
        setGeneralErrors(messages);
        enqueueSnackbar("Errores de validacion en el formulario.", {
          variant: "warning",
        });
      } else if (err.status === 404) {
        setGeneralErrors([
          "El patrocinador que intentas modificar ya no existe.",
        ]);
        enqueueSnackbar("El patrocinador no existe o fue eliminado.", {
          variant: "warning",
        });
      } else {
        setGeneralErrors([err.message || "Error al guardar patrocinador."]);
        enqueueSnackbar(err.message || "Error en el servidor.", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sponsor) => {
    const normalizedLinks = (sponsor.links || []).map((link) => ({
      title: link.title,
      url: link.url,
    }));
    const { lada, phone } = stripKnownLada(sponsor.phone);

    setForm({
      business_name: sponsor.business_name ?? "",
      address: sponsor.address ?? "",
      phone,
      phone_lada: lada,
      notes: sponsor.notes ?? "",
    });
    setEditingId(sponsor.id);
    setLinks(normalizedLinks);
    setPhotoFile(null);
    setFieldErrors({});
    setGeneralErrors([]);
  };

  const confirmDelete = (id) => {
    setSponsorToDelete(id);
    setConfirmOpen(true);
  };

  const deleteSponsor = async () => {
    setLoading(true);
    try {
      await deleteSponsorById(sponsorToDelete);
      enqueueSnackbar("Patrocinador eliminado correctamente.", {
        variant: "success",
      });
      await refreshSponsors();
    } catch (err) {
      if (err.status === 404) {
        setSponsors((prev) =>
          prev.filter((sponsor) => sponsor.id !== sponsorToDelete),
        );
        enqueueSnackbar("El patrocinador ya no existe.", {
          variant: "warning",
        });
      } else {
        enqueueSnackbar(err.message || "Error al eliminar patrocinador.", {
          variant: "error",
        });
      }
    } finally {
      setConfirmOpen(false);
      setSponsorToDelete(null);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        mb: 4,
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 4, sm: 6 },
        borderRadius: "24px",
        backgroundColor: "#fcffff",
        border: "3px solid #e0f2e9",
        boxShadow: "0 8px 32px rgba(27, 141, 75, 0.08)",
      }}
    >
      <Typography
        variant="h2"
        component="h1"
        sx={{
          fontSize: "2.5rem",
          fontFamily: "'Comic Relief', cursive",
          fontWeight: 700,
          color: "#1B8D4B",
          mb: 3,
        }}
      >
        Panel de Patrocinadores
      </Typography>

      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
        <Box flex={1} sx={{ minWidth: "300px" }}>
          <SponsorForm
            countryCodes={countryCodes}
            editingId={editingId}
            fieldErrors={fieldErrors}
            form={form}
            generalErrors={generalErrors}
            linkForm={linkForm}
            links={links}
            onAddLink={handleAddLink}
            onCancel={resetForm}
            onChange={handleChange}
            onDeleteLink={handleDeleteLink}
            onFileChange={handleFileChange}
            onLinkChange={handleLinkChange}
            onSubmit={handleSubmit}
            photoFile={photoFile}
          />
        </Box>

        <Box flex={1}>
          <SponsorList
            currentPage={currentPage}
            currentSponsors={currentSponsors}
            expandedSponsorId={expandedSponsorId}
            filteredSponsors={filteredSponsors}
            itemsPerPage={itemsPerPage}
            onDelete={confirmDelete}
            onEdit={handleEdit}
            onPageChange={(_, value) => setCurrentPage(value)}
            onSearchChange={(e) => {
              setSearchTerm(e.target.value.toLowerCase());
              setCurrentPage(1);
            }}
            onToggleExpand={(id) =>
              setExpandedSponsorId((prev) => (prev === id ? null : id))
            }
            totalPages={totalPages}
          />
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            border: "2px solid #e0f2e9",
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Comic Relief', cursive",
            color: "#1B8D4B",
            fontWeight: 600,
          }}
        >
          Estas seguro de que deseas eliminar este patrocinador?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              color: "#555",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={deleteSponsor}
            color="error"
            variant="contained"
            sx={{
              backgroundColor: "#ff4444",
              "&:hover": {
                backgroundColor: "#cc0000",
              },
              borderRadius: "50px",
              px: 3,
            }}
          >
            Confirmar eliminacion
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
